import React, { useEffect, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const About = () => {
    const playerRef = useRef(null);
    const VIDEO_ID = 'HhN5FKRYvW8';

    // show poster first; create the player only when user clicks play
    const [isPlayerActive, setIsPlayerActive] = useState(false);
    const [ytReady, setYtReady] = useState(false);

    // load YouTube API on mount
    useEffect(() => {
        if (window.YT && window.YT.Player) {
            setYtReady(true);
            return;
        }

        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(tag);

        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (prev) prev();
            setYtReady(true);
        };

        return () => {
            // don't remove global handlers; just leave the script
            window.onYouTubeIframeAPIReady = prev;
        };
    }, []);

    // create/destroy player when isPlayerActive toggles
    useEffect(() => {
        let mounted = true;

        function onPlayerStateChange(event) {
            if (!mounted) return;
            // ENDED
            if (event.data === window.YT?.PlayerState?.ENDED) {
                // destroy player and show poster to avoid YouTube endscreen suggestions
                if (playerRef.current && playerRef.current.destroy) {
                    playerRef.current.destroy();
                    playerRef.current = null;
                }
                setIsPlayerActive(false);
            }
        }

        function createPlayer(autoplay = false) {
            if (playerRef.current) return;
            playerRef.current = new window.YT.Player('about-video-player', {
                height: '100%',
                width: '100%',
                videoId: VIDEO_ID,
                playerVars: {
                    rel: 0,
                    modestbranding: 1,
                    controls: 1,
                    enablejsapi: 1,
                    autoplay: autoplay ? 1 : 0
                },
                events: {
                    onStateChange: onPlayerStateChange
                }
            });
        }

        if (isPlayerActive && ytReady) {
            createPlayer(true);
        }

        return () => {
            mounted = false;
            // keep player alive only while active; if leaving component, destroy
            if (playerRef.current && playerRef.current.destroy) {
                playerRef.current.destroy();
                playerRef.current = null;
            }
        };
    }, [isPlayerActive, ytReady]);

    return (
        <Box sx={{
            p: '1rem',
            pb: '2rem',
            backgroundColor: '#EEF6F5'
        }}>
            <Box sx={{
                maxWidth: '85rem',
                margin: 'auto'
            }}>
                <Typography variant='h5' sx={(theme) => ({
                    color: 'text.secondary',
                    fontWeight: '100',
                    [`${theme.breakpoints.up('sm')}`]: {
                        marginBottom: '3rem'
                    }
                })}>
                    About MediRadar
                </Typography>
                <Box sx={(theme) => ({
                    display: 'flex',
                    gap: '3rem',
                    padding: 0,
                    alignItems: 'center',
                    [`${theme.breakpoints.down('md')}`]: {                        
                        flexWrap: 'wrap',
                    }
                })}>
                    <Box sx={(theme) => ({
                        width: '100%',
                        flexBasis: '100%',
                        [`${theme.breakpoints.up('md')}`]: {
                            flexBasis: '50%'
                        }
                    })}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}>
                            <Box sx={{
                                width: '100%',
                                maxWidth: 480,
                                borderRadius: '1.5rem',
                                overflow: 'hidden',
                                background: 'transparent',
                                mb: 2
                            }}>
                                <Box sx={{
                                    position: 'relative',
                                    paddingBottom: '57.25%',
                                    height: 0
                                }}>
                                    {/* YouTube IFrame API player container */}
                                    <div id="about-video-wrapper" style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%'
                                    }}>
                                        <div id="about-video-player" style={{
                                            width: '100%',
                                            height: '100%'
                                        }} />

                                        {!isPlayerActive ? (
                                            <div id="about-video-overlay"
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                {/* Poster image */}
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    backgroundImage: `url(https://i.ytimg.com/vi/${VIDEO_ID}/maxresdefault.jpg)`,
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center'
                                                }} />

                                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'auto' }}>
                                                    <Button 
                                                        onClick={() => setIsPlayerActive(true)} 
                                                        sx={{ 
                                                            borderRadius: '50%',
                                                            width: '80px',
                                                            height: '80px',
                                                            minWidth: '80px',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                            color: '#fff',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                                                            transition: 'all 0.3s ease',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                                                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                                                                transform: 'scale(1.1)'
                                                            }
                                                        }}
                                                    >
                                                        <PlayArrowIcon sx={{ fontSize: '48px', marginLeft: '4px' }} />
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={(theme) => ({
                        flexBasis: '100%',
                        [`${theme.breakpoints.up('md')}`]: {
                            flexBasis: '50%'
                        }
                    })}>
                        <Typography sx={() => ({
                            overflow: 'hidden',
                            position: 'relative',
                        })}>                        
                            <p>
                                MediRadar is an online platform dedicated to improving access 
                                to high quality medical imaging for patients, through our customer friendly website, 
                                and easy search and bookings process.
                            </p>
                            <p>
                                MediRadar allows patients to search for affordable, state of the art imaging facilities near them,
                                and book scans immediately with rapid confirmation of appointments. 
                                This allows patients to get answers quickly and avoid long wait times within the NHS. 
                                The platform allows patient customers to also link in with relevant specialists, 
                                before or after their scan through a video consultation, or face-to-face consultation near them
                            </p>
                            <p>
                                Our passion is to deliver a seamless experience, enabling the first steps towards recovery and better health.
                                MediRadar can assist patients embarking on health promotion and screening, which can enable early detection of treatable conditions.
                                MediRadar, empowering patients, improving health.
                            </p>
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default About;